import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { performTokenRefresh } from '../services/authService';
import { userService } from '../services/userService';

const AuthContext = createContext();
const normalizeRoles = (roles = []) => roles.map((role) => role.replace('ROLE_', ''));
const requiresEmployeeProfile = (userData) => {
  const roles = normalizeRoles(userData?.roles);
  return roles.includes('EMPLOYEE') || roles.includes('RECEPTION');
};
const isValidUserSession = (userData) => !requiresEmployeeProfile(userData) || userData?.employeeId != null;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const getNormalizedRoles = () => normalizeRoles(user?.roles);

  const clearStoredAuth = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
  };

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);

        if (isValidUserSession(parsedUser)) {
          setUser(parsedUser);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
          clearStoredAuth();
        }
      } catch (error) {
        clearStoredAuth();
      }
    }
    
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/signin', {
        username,
        password
      });

      const { accessToken, refreshToken, id, employeeId, username: userUsername, email, roles, tutorialStatus, whatsNewSeenVersion } = response.data;

      const userData = {
        id,
        employeeId,
        username: userUsername,
        email,
        roles,
        tutorialStatus,
        whatsNewSeenVersion,
      };

      if (!isValidUserSession(userData)) {
        clearStoredAuth();
        setUser(null);
        return {
          success: false,
          error: 'Your account is not linked to an employee profile'
        };
      }

      localStorage.setItem('token', accessToken);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      setUser(userData);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Email or password were incorrect' 
      };
    }
  };

  const logout = () => {
    clearStoredAuth();
    setUser(null);
  };

  const markTutorialStatus = async (status) => {
    try {
      await userService.updateTutorialStatus(status);
      const updatedUser = { ...user, tutorialStatus: status };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Failed to update tutorial status:', error);
    }
  };

  const markWhatsNewSeen = async (version) => {
    try {
      await userService.markWhatsNewSeen(version);
      const updatedUser = { ...user, whatsNewSeenVersion: version };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Failed to update whats new seen version:', error);
    }
  };

  const refreshToken = async () => {
    try {
      const newToken = await performTokenRefresh();
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true };
    } catch {
      clearStoredAuth();
      setUser(null);
      return { success: false };
    }
  };

  const hasRole = (role) => getNormalizedRoles().includes(role);

  const isAdmin = () => hasRole('ADMIN');

  const isHR = () => hasRole('HR');

  const isReception = () => hasRole('RECEPTION');

  const canManageDepartments = () => isAdmin() || isHR();

  const canAccessEmployees = () => isAdmin() || isHR();

  const canApproveLeaveRequests = () => isAdmin() || isHR();

  const canManageEmployees = () => canAccessEmployees();

  const canViewDashboard = () => isAdmin() || isHR() || isReception();

  const canViewDepartments = () => isAdmin() || isHR() || isReception();

  const canFilterCalendarByDepartment = () => isAdmin() || isHR() || isReception();

  const getDefaultRoute = () => (canViewDashboard() ? '/dashboard' : '/leave');

  const value = {
    user,
    login,
    logout,
    refreshToken,
    loading,
    hasRole,
    isAdmin,
    isHR,
    isReception,
    canManageDepartments,
    canAccessEmployees,
    canApproveLeaveRequests,
    canManageEmployees,
    canViewDashboard,
    canViewDepartments,
    canFilterCalendarByDepartment,
    getDefaultRoute,
    markTutorialStatus,
    markWhatsNewSeen,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
